/* Haavi — seamless scrolling marquee.
 * Fills the track with enough copies of its items so a CSS translateX(-50%) loop is
 * seamless (no gap) regardless of how few items the merchant adds, and keeps the scroll
 * SPEED constant by scaling the animation duration with the number of copies.
 *
 * Robustness notes:
 *  - Retries with setInterval (NOT requestAnimationFrame — rAF is paused/throttled in
 *    background or not-yet-visible tabs, which left the clone incomplete).
 *  - Waits for document.fonts.ready before measuring, so item widths are final (measuring
 *    with the fallback font gives the wrong copy count and leaves a loop gap).
 *  - build() is idempotent (guarded by data-ho-cloned). */
class HoMarquee extends HTMLElement {
  connectedCallback() {
    const kickoff = () => {
      if (this.build()) return;
      let tries = 0;
      this._iv = setInterval(() => {
        if (this.build() || ++tries > 40) { clearInterval(this._iv); this._iv = null; }
      }, 200);
    };
    const ready = document.fonts && document.fonts.ready;
    if (ready && typeof ready.then === 'function') {
      let started = false;
      const start = () => { if (!started) { started = true; kickoff(); } };
      ready.then(start);
      setTimeout(start, 1800); // fallback if fonts.ready is slow or never resolves
    } else {
      kickoff();
    }
  }

  disconnectedCallback() {
    if (this._iv) { clearInterval(this._iv); this._iv = null; }
  }

  #track() {
    return this.querySelector('[data-marquee-track]') || this.querySelector('.ho-announcement__track');
  }

  // Returns true when done (cloned, or already cloned), false when not ready yet.
  build() {
    const track = this.#track();
    if (!track) return false;
    if (track.dataset.hoCloned) return true;

    const originals = Array.from(track.children);
    const width = this.getBoundingClientRect().width;
    if (originals.length === 0 || width === 0) return false;

    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    let setWidth = 0;
    originals.forEach((el) => { setWidth += el.getBoundingClientRect().width + gap; });

    const baseDuration = parseFloat(styles.animationDuration) || 26;
    // Enough copies of the item-set for the first half to cover the viewport (no gap).
    const reps = Math.max(1, Math.ceil(width / Math.max(setWidth, 1)));

    for (let i = 0; i < reps - 1; i++) {
      originals.forEach((el) => track.appendChild(el.cloneNode(true)));
    }
    // Duplicate the whole first half to make the seamless second half.
    Array.from(track.children).forEach((el) => {
      const c = el.cloneNode(true);
      c.setAttribute('aria-hidden', 'true');
      track.appendChild(c);
    });
    // Keep per-set scroll speed constant: the loop covers `reps` sets, so scale the duration.
    if (reps > 1) track.style.animationDuration = (reps * baseDuration) + 's';

    track.dataset.hoCloned = '1';
    return true;
  }
}

if (!customElements.get('ho-marquee')) customElements.define('ho-marquee', HoMarquee);
